import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ComponentRef,
	HostBinding,
	Type,
	ViewChild,
	ViewContainerRef
} from "@angular/core";
import { UniversalModalService } from "@modules/+new/shared/universal-modal/universal-modal.service";
import { filter } from "rxjs/operators";
import { ModalOptions } from "@modules/+new/shared/universal-modal/entities/modal-options.interface";
import { ModalDefinition } from "@modules/+new/shared/universal-modal/entities/modal-definition.interface";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { PresetsTransforms } from "@modules/+new/shared/universal-modal/entities/presets-transforms.type";
import { trigger } from "@angular/animations";
import { openingClosing } from "../../../../../../animations/animations";

/***
 * @description ModalHolderComponent shouldn't contain any logic beside how to handle views and render components.
 * Also this component shouldn't contain any information from ModalList as well.
 */
enum ModalTargetType {
	body = "body",
	header = "header",
	footer = "footer"
}
@UntilDestroy()
@Component({
	selector: "modal-render",
	template: `
		<div class="modal" @openingClosingTrigger>
			<div class="modal-header">
				<ng-container #header></ng-container>
			</div>
			<div class="modal-body">
				<ng-container #body></ng-container>
			</div>
			<ng-container #onlyContentBody></ng-container>
			<div class="modal-footer">
				<ng-container #footer></ng-container>
			</div>
		</div>
	`,
	styleUrls: ["./render-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [trigger("openingClosingTrigger", openingClosing)],
	standalone: false
})
export class RenderModalComponent implements AfterViewInit {
	@ViewChild("body", { read: ViewContainerRef }) public body: ViewContainerRef;
	@ViewChild("onlyContentBody", { read: ViewContainerRef }) public onlyContentBody: ViewContainerRef;
	@ViewChild("header", { read: ViewContainerRef }) public header: ViewContainerRef;
	@ViewChild("footer", { read: ViewContainerRef }) public footer: ViewContainerRef;
	public options: ModalOptions;
	@HostBinding("class") public classes: string;

	public constructor(
		private modalService: UniversalModalService,
		private cdr: ChangeDetectorRef
	) {}

	private payloadChange({ component, options, data, transforms }: ModalDefinition): void {
		this.options = options;
		this.clearViews();
		this.appendViews(component, data, transforms);
	}

	private getClasses = (options: ModalOptions = this.options): string => {
		if (!this.options) {
			return;
		}
		const { extraClass, size } = options;
		return `size-${size} ${extraClass ? extraClass.join(" ") : ""}`;
	};

	private clearViews = (): void => {
		this.header.clear();
		this.body.clear();
		this.onlyContentBody.clear();
		this.footer.clear();
	};

	// TODO: eslint fix
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	private appendViews = (component: Type<any>, data?: unknown, transforms?: PresetsTransforms): void => {
		const { wrap, headerComponent, footerComponent } = this.options;
		wrap && headerComponent && this.render(headerComponent, ModalTargetType.header);
		wrap && footerComponent && this.render(footerComponent, ModalTargetType.footer);
		this.render(component, ModalTargetType.body, data, transforms);
	};

	private pickVcr = (target: ModalTargetType): ViewContainerRef => {
		const { wrap } = this.options;
		switch (target) {
			case ModalTargetType.body:
				return wrap ? this.body : this.onlyContentBody;
			case ModalTargetType.header:
				return this.header;
			case ModalTargetType.footer:
				return this.footer;
			default:
				return this.body;
		}
	};

	private render(
		// TODO: eslint fix
		/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
		component: Type<any>,
		target: ModalTargetType,
		data?: unknown,
		transforms?: PresetsTransforms
	): void {
		const vcr = this.pickVcr(target);
		const comp = vcr.createComponent(component);
		this.appendData(comp, target, data, transforms);
		this.classes = this.getClasses();
	}

	private appendData(
		// TODO: eslint fix
		/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
		componentRef: ComponentRef<any>,
		target: ModalTargetType,
		data?: unknown,
		transforms?: PresetsTransforms
	): void {
		// we don't want business logic in dumb components - so data is only for main component
		if (target !== ModalTargetType.body) {
			return;
		}
		componentRef.instance.data = data;
		componentRef.instance.transforms = transforms;

		//TODO temporary workaround for modals size issue, should be fixed in FADW-1768
		setTimeout(() => componentRef.changeDetectorRef.markForCheck(), 1);
	}

	public ngAfterViewInit(): void {
		this.modalService.current$
			.pipe(
				untilDestroyed(this),
				filter((v) => !!v?.definition?.options)
			)
			.subscribe(({ definition }) => {
				this.payloadChange(definition);
				// fixme: below line is seriously messed up. triggering another round of CD shouldn't be held
				// in AfterViewInit. it will be tackled on next task FADW-1768
				setTimeout(() => this.cdr.detectChanges(), 1);
			});
	}
}
